import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.games');
import { AfterViewInit, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Service } from "src/libs/portal/season/service";

type GameKey = 'frog' | 'snake' | 'runner';

interface GameMeta {
    key: GameKey;
    label: string;
    short: string;
    description: string;
    difficulty: string;
    controls: string[];
    unityPitch: string;
}

interface HudState {
    score: number;
    best: number;
    detailLabel: string;
    detailValue: string;
    modeLabel: string;
    modeValue: string;
    status: string;
    actionHint: string;
}

interface GameRuntime {
    key: GameKey;
    reset(): void;
    update(dt: number): void;
    draw(ctx: CanvasRenderingContext2D): void;
    keydown(event: KeyboardEvent): void;
    destroy?(): void;
}

interface Rect {
    x: number;
    y: number;
    width: number;
    height: number;
}

@Component({
    selector: 'wiz-page-games',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.games/view.scss */
:host {
  display: block;
  height: 100%;
}

canvas {
  image-rendering: pixelated;
  touch-action: manipulation;
}`],
})
export class PageGamesComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('gameCanvas') public gameCanvas?: ElementRef<HTMLCanvasElement>;

    public games: GameMeta[] = [
        {
            key: 'frog',
            label: 'Frog Rush',
            short: '차선과 연못을 건너 연잎 5개를 먼저 점령하세요.',
            description: '클래식 Frogger 감각을 그대로 가져온 미니게임입니다. 자동차와 통나무 패턴을 읽으면서 위쪽 연잎을 채우면 속도가 점점 올라갑니다.',
            difficulty: '중간',
            controls: [
                '방향키 또는 WASD로 한 칸씩 이동',
                '모바일에서는 아래 터치 패드 사용',
                '게임 오버 시 Enter 또는 다시 시작'
            ],
            unityPitch: 'Unity 2D에서 타일맵, Rigidbody2D, lane 스포너만 붙이면 WebGL 버전으로 옮기기 쉬운 구조입니다.'
        },
        {
            key: 'snake',
            label: 'Snake Grid',
            short: '먹이를 먹고 점점 빨라지는 네온 스네이크를 조종하세요.',
            description: '리듬감 있게 방향만 잘 바꾸면 오래 버틸 수 있는 스네이크입니다. 먹이를 먹을 때마다 길이와 속도가 함께 올라갑니다.',
            difficulty: '보통',
            controls: [
                '방향키 또는 WASD로 진행 방향 변경',
                '벽이나 자기 몸에 닿으면 종료',
                '게임 오버 시 Enter 또는 다시 시작'
            ],
            unityPitch: 'Unity 2D Grid와 ScriptableObject 점수 규칙으로 빠르게 재구성 가능한 가장 단순한 프로토타입입니다.'
        },
        {
            key: 'runner',
            label: 'Sky Runner',
            short: '속도가 붙는 러너 트랙에서 점프 타이밍만으로 버텨보세요.',
            description: '한 번 감이 오면 계속 달리게 되는 원버튼 러너입니다. 속도가 조금씩 올라가고, 장애물을 넘길수록 연속 클리어 수가 쌓입니다.',
            difficulty: '쉬움',
            controls: [
                '위 방향키, W 또는 Space로 점프',
                '속도가 붙을수록 점프 타이밍이 더 중요',
                '게임 오버 시 Enter 또는 다시 시작'
            ],
            unityPitch: 'Unity 2D Endless Runner 템플릿으로 가장 빠르게 옮길 수 있는 장르이며, WebGL 빌드에 특히 잘 맞습니다.'
        }
    ];

    public controlButtons: any[] = [
        { key: 'ArrowUp', label: '↑', hint: '위 / 점프' },
        { key: 'ArrowLeft', label: '←', hint: '왼쪽' },
        { key: 'ArrowRight', label: '→', hint: '오른쪽' },
        { key: 'ArrowDown', label: '↓', hint: '아래' },
        { key: ' ', label: 'Jump', hint: 'Space' },
        { key: 'Enter', label: 'Reset', hint: 'Enter' },
    ];

    public currentGameKey: GameKey = this.normalizeGame(WizRoute.segment.game || 'frog');
    public selectedGame: GameMeta = this.getGameMeta(this.currentGameKey);
    public hud: HudState = {
        score: 0,
        best: 0,
        detailLabel: '목숨',
        detailValue: '3',
        modeLabel: '상태',
        modeValue: '-',
        status: '게임을 불러오는 중입니다.',
        actionHint: '잠시만 기다려주세요.'
    };

    private ctx: CanvasRenderingContext2D | null = null;
    private runtime: GameRuntime | null = null;
    private initialized: boolean = false;
    private viewReady: boolean = false;
    private renderQueued: boolean = false;
    private animationFrameId: any = null;
    private lastFrameTime: number = 0;
    private routerSub: any = null;

    constructor(@Inject( Service) public service: Service,@Inject( Router)  private router: Router) { }

    public async ngOnInit() {
        await this.service.init();

        this.initialized = true;
        this.routerSub = this.router.events.subscribe(async (event: any) => {
            if (event instanceof NavigationEnd) {
                await this.syncRouteGame();
            }
        });

        await this.syncRouteGame();
    }

    public async ngAfterViewInit() {
        this.viewReady = true;

        const canvas = this.gameCanvas?.nativeElement;
        if (!canvas) return;

        this.ctx = canvas.getContext('2d');
        this.startLoop();

        if (this.initialized) {
            await this.activateGame(this.currentGameKey);
        }
    }

    public ngOnDestroy() {
        if (this.routerSub) this.routerSub.unsubscribe();
        if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
        if (this.runtime && this.runtime.destroy) this.runtime.destroy();
    }

    @HostListener('window:keydown', ['$event'])
    public handleKeydown(event: KeyboardEvent) {
        const keys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' ', 'Spacebar', 'w', 'W', 'a', 'A', 's', 'S', 'd', 'D'];
        if (keys.indexOf(event.key) !== -1 || event.code === 'Space') {
            event.preventDefault();
        }
        if (!this.runtime) return;
        this.runtime.keydown(event);
    }

    public openGame(key: GameKey) {
        if (key === this.currentGameKey && this.runtime) {
            this.runtime.reset();
            return;
        }
        this.service.href(`/games/${key}`);
    }

    public openQuizRunner() {
        this.service.href('/quiz-runner');
    }

    public restartCurrentGame() {
        if (!this.runtime) return;
        this.runtime.reset();
    }

    public pressPad(key: string) {
        if (!this.runtime) return;

        const fakeEvent = {
            key: key,
            code: key === ' ' ? 'Space' : key,
            repeat: false,
            preventDefault() { }
        } as KeyboardEvent;

        this.runtime.keydown(fakeEvent);
    }

    public gameButtonClass(key: GameKey) {
        if (key === this.currentGameKey) {
            return 'border-transparent bg-zinc-950 text-white shadow-lg shadow-zinc-900/15';
        }
        return 'border-gray-200 bg-white text-gray-900 hover:border-gray-300 hover:bg-gray-50';
    }

    public difficultyClass(key: GameKey) {
        switch (key) {
            case 'frog':
                return 'bg-emerald-100 text-emerald-700';
            case 'snake':
                return 'bg-sky-100 text-sky-700';
            case 'runner':
                return 'bg-amber-100 text-amber-700';
        }
    }

    public heroClass() {
        switch (this.currentGameKey) {
            case 'frog':
                return 'bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600';
            case 'snake':
                return 'bg-gradient-to-br from-sky-500 via-indigo-500 to-violet-600';
            case 'runner':
                return 'bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500';
        }
    }

    public noteClass() {
        switch (this.currentGameKey) {
            case 'frog':
                return 'border-emerald-500 bg-gradient-to-br from-emerald-600 to-teal-700';
            case 'snake':
                return 'border-sky-500 bg-gradient-to-br from-sky-600 to-indigo-700';
            case 'runner':
                return 'border-amber-500 bg-gradient-to-br from-amber-500 to-orange-600';
        }
    }

    private async syncRouteGame() {
        const requested = WizRoute.segment.game;
        const normalized = this.normalizeGame(requested || 'frog');

        if (!requested) {
            this.service.href('/games/frog');
            return;
        }

        if (requested !== normalized) {
            this.service.href(`/games/${normalized}`);
            return;
        }

        if (normalized !== this.currentGameKey || !this.runtime) {
            await this.activateGame(normalized);
        }
    }

    private async activateGame(key: GameKey) {
        this.currentGameKey = key;
        this.selectedGame = this.getGameMeta(key);

        if (this.runtime && this.runtime.destroy) {
            this.runtime.destroy();
        }

        this.runtime = this.buildRuntime(key);
        this.runtime.reset();
        this.queueRender();
    }

    private buildRuntime(key: GameKey): GameRuntime {
        switch (key) {
            case 'snake':
                return this.createSnakeRuntime();
            case 'runner':
                return this.createRunnerRuntime();
            default:
                return this.createFrogRuntime();
        }
    }

    private startLoop() {
        if (this.animationFrameId || !this.ctx) return;

        this.lastFrameTime = performance.now();
        const tick = (time: number) => {
            const dt = Math.min((time - this.lastFrameTime) / 1000, 0.05);
            this.lastFrameTime = time;

            if (this.runtime && this.ctx) {
                this.runtime.update(dt);
                this.runtime.draw(this.ctx);
            } else if (this.ctx) {
                this.drawLoadingState(this.ctx);
            }

            this.animationFrameId = requestAnimationFrame(tick);
        };

        this.animationFrameId = requestAnimationFrame(tick);
    }

    private drawLoadingState(ctx: CanvasRenderingContext2D) {
        ctx.clearRect(0, 0, 720, 576);
        ctx.fillStyle = '#020617';
        ctx.fillRect(0, 0, 720, 576);
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.font = '600 22px SUIT';
        ctx.textAlign = 'center';
        ctx.fillText('게임을 준비하는 중...', 360, 288);
    }

    private updateHud(patch: Partial<HudState>) {
        let changed = false;
        for (const [key, value] of Object.entries(patch)) {
            if ((this.hud as any)[key] !== value) {
                (this.hud as any)[key] = value;
                changed = true;
            }
        }

        if (changed) {
            this.queueRender();
        }
    }

    private queueRender() {
        if (!this.initialized || this.renderQueued) return;

        this.renderQueued = true;
        Promise.resolve().then(async () => {
            this.renderQueued = false;
            await this.service.render();
        });
    }

    private normalizeGame(value: string): GameKey {
        if (value === 'snake' || value === 'runner' || value === 'frog') {
            return value;
        }
        return 'frog';
    }

    private getGameMeta(key: GameKey): GameMeta {
        const found = this.games.find((game) => game.key === key);
        return found || this.games[0];
    }

    private storageKey(key: GameKey) {
        return `wiz-sample-best-${key}`;
    }

    private readBestScore(key: GameKey): number {
        try {
            const raw = Number(window.localStorage.getItem(this.storageKey(key)) || '0');
            return Number.isFinite(raw) ? raw : 0;
        } catch {
            return 0;
        }
    }

    private commitBestScore(key: GameKey, score: number): number {
        const next = Math.max(this.readBestScore(key), Math.floor(score));
        try {
            window.localStorage.setItem(this.storageKey(key), String(next));
        } catch { }
        return next;
    }

    private clamp(value: number, min: number, max: number) {
        return Math.max(min, Math.min(max, value));
    }

    private intersects(a: Rect, b: Rect) {
        return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
    }

    private drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
    }

    private createFrogRuntime(): GameRuntime {
        const width = 720;
        const height = 576;
        const cell = 48;
        const frogSize = 30;
        const goalCenters = [72, 216, 360, 504, 648];
        const goalRadius = 32;

        const laneBlueprints: any[] = [
            { type: 'water', row: 2, speed: 92, color: '#92400e', xs: [-40, 240, 520], width: 148, height: 30 },
            { type: 'water', row: 3, speed: -108, color: '#78350f', xs: [40, 330, 610], width: 128, height: 30 },
            { type: 'water', row: 4, speed: 126, color: '#854d0e', xs: [-90, 150, 470], width: 112, height: 30 },
            { type: 'road', row: 6, speed: -170, color: '#f97316', xs: [30, 270, 530], width: 88, height: 30 },
            { type: 'road', row: 7, speed: 210, color: '#38bdf8', xs: [-70, 220, 470], width: 74, height: 30 },
            { type: 'road', row: 8, speed: -235, color: '#f43f5e', xs: [100, 350, 620], width: 96, height: 30 },
            { type: 'road', row: 9, speed: 148, color: '#a78bfa', xs: [-40, 250, 520], width: 104, height: 30 },
        ];

        let frog = { x: 0, y: 0 };
        let lives = 3;
        let score = 0;
        let level = 1;
        let filledPads = [false, false, false, false, false];
        let respawnTimer = 0;
        let gameOver = false;
        let status = '방향키로 이동해 연잎 다섯 개를 모두 점령하세요.';
        let lanes = this.cloneLanes(laneBlueprints, cell);

        const placeFrog = () => {
            frog.x = width / 2 - frogSize / 2;
            frog.y = height - cell + (cell - frogSize) / 2;
        };

        const scaledSpeed = (base: number) => {
            return base * (1 + (level - 1) * 0.14);
        };

        const syncHud = (actionHint?: string) => {
            this.updateHud({
                score: Math.floor(score),
                best: this.commitBestScore('frog', score),
                detailLabel: '목숨',
                detailValue: String(lives),
                modeLabel: '연잎',
                modeValue: `${filledPads.filter(Boolean).length} / ${filledPads.length}`,
                status: status,
                actionHint: gameOver ? 'Enter 또는 다시 시작으로 재도전하세요.' : (actionHint || '방향키 또는 화면 패드로 이동')
            });
        };

        const loseLife = (message: string) => {
            if (respawnTimer > 0 || gameOver) return;

            lives -= 1;
            status = message;

            if (lives <= 0) {
                lives = 0;
                gameOver = true;
                syncHud();
                return;
            }

            placeFrog();
            respawnTimer = 0.75;
            syncHud('잠깐 숨을 고른 뒤 다시 건너보세요.');
        };

        const clearLevel = () => {
            level += 1;
            score += 250;
            filledPads = [false, false, false, false, false];
            lanes = this.cloneLanes(laneBlueprints, cell);
            placeFrog();
            respawnTimer = 0.55;
            status = `모든 연잎 확보 완료. ${level}레벨로 속도가 올라갑니다.`;
            syncHud('패턴이 더 빨라졌습니다. 이동 타이밍을 조금 앞당겨보세요.');
        };

        const claimGoal = () => {
            const centerX = frog.x + frogSize / 2;
            const slotIndex = goalCenters.findIndex((goal) => Math.abs(goal - centerX) <= goalRadius);

            if (slotIndex === -1 || filledPads[slotIndex]) {
                loseLife('연잎에 정확히 착지하지 못했습니다.');
                return;
            }

            filledPads[slotIndex] = true;
            score += 100;

            if (filledPads.every((filled) => filled === true)) {
                clearLevel();
                return;
            }

            placeFrog();
            respawnTimer = 0.45;
            status = '연잎 하나를 확보했습니다. 남은 칸을 계속 채워보세요.';
            syncHud();
        };

        const moveFrog = (dx: number, dy: number) => {
            if (respawnTimer > 0 || gameOver) return;

            frog.x = this.clamp(frog.x + dx * cell, 0, width - frogSize);
            frog.y = this.clamp(frog.y + dy * cell, 0, height - frogSize);

            if (dy < 0) {
                score += 5;
            }

            if (frog.y < cell) {
                claimGoal();
                return;
            }

            syncHud();
        };

        return {
            key: 'frog',

            reset: () => {
                lives = 3;
                score = 0;
                level = 1;
                filledPads = [false, false, false, false, false];
                respawnTimer = 0;
                gameOver = false;
                lanes = this.cloneLanes(laneBlueprints, cell);
                placeFrog();
                status = '방향키로 이동해 연잎 다섯 개를 모두 점령하세요.';
                syncHud();
            },

            keydown: (event: KeyboardEvent) => {
                if ((event.key === 'Enter' || event.code === 'Space') && gameOver) {
                    event.preventDefault();
                    this.runtime?.reset();
                    return;
                }

                if (event.repeat) return;

                const key = event.key;
                if (key === 'Enter') {
                    this.runtime?.reset();
                    return;
                }
                if (key === 'ArrowUp' || key === 'w' || key === 'W') moveFrog(0, -1);
                if (key === 'ArrowDown' || key === 's' || key === 'S') moveFrog(0, 1);
                if (key === 'ArrowLeft' || key === 'a' || key === 'A') moveFrog(-1, 0);
                if (key === 'ArrowRight' || key === 'd' || key === 'D') moveFrog(1, 0);
            },

            update: (dt: number) => {
                if (gameOver) return;

                if (respawnTimer > 0) {
                    respawnTimer = Math.max(0, respawnTimer - dt);
                    return;
                }

                lanes.forEach((lane) => {
                    const velocity = scaledSpeed(lane.speed);
                    lane.entities.forEach((entity: any) => {
                        entity.x += velocity * dt;

                        if (velocity > 0 && entity.x > width + entity.width + 120) {
                            entity.x = -entity.width - Math.random() * 140;
                        }

                        if (velocity < 0 && entity.x < -entity.width - 120) {
                            entity.x = width + Math.random() * 140;
                        }
                    });
                });

                const lane = lanes.find((item) => {
                    const centerY = frog.y + frogSize / 2;
                    return centerY >= item.y && centerY < item.y + cell;
                });

                if (!lane) return;

                if (lane.type === 'road') {
                    const collision = lane.entities.some((entity: any) => {
                        return this.intersects(
                            { x: frog.x, y: frog.y, width: frogSize, height: frogSize },
                            { x: entity.x, y: lane.y + 9, width: entity.width, height: entity.height }
                        );
                    });

                    if (collision) {
                        loseLife('자동차에 부딪혔습니다.');
                    }
                }

                if (lane.type === 'water') {
                    const support = lane.entities.find((entity: any) => {
                        return this.intersects(
                            { x: frog.x, y: frog.y, width: frogSize, height: frogSize },
                            { x: entity.x, y: lane.y + 9, width: entity.width, height: entity.height }
                        );
                    });

                    if (!support) {
                        loseLife('연못에 빠졌습니다.');
                        return;
                    }

                    frog.x += scaledSpeed(lane.speed) * dt;
                    if (frog.x < -frogSize || frog.x > width) {
                        loseLife('통나무에서 미끄러졌습니다.');
                    }
                }
            },

            draw: (ctx: CanvasRenderingContext2D) => {
                ctx.clearRect(0, 0, width, height);

                ctx.fillStyle = '#021420';
                ctx.fillRect(0, 0, width, height);

                for (let row = 0; row < 12; row++) {
                    let color = '#14532d';
                    if (row === 0) color = '#0f766e';
                    if (row === 1 || row === 5 || row === 10 || row === 11) color = '#166534';
                    if (row >= 2 && row <= 4) color = '#0f3d57';
                    if (row >= 6 && row <= 9) color = '#202734';

                    ctx.fillStyle = color;
                    ctx.fillRect(0, row * cell, width, cell);
                }

                ctx.fillStyle = 'rgba(255,255,255,0.08)';
                for (let i = 0; i < 14; i++) {
                    ctx.fillRect(i * 52, 0, 16, 6);
                }

                goalCenters.forEach((goal, index) => {
                    ctx.beginPath();
                    ctx.fillStyle = filledPads[index] ? '#86efac' : '#bbf7d0';
                    ctx.arc(goal, 24, filledPads[index] ? 18 : 14, 0, Math.PI * 2);
                    ctx.fill();

                    if (filledPads[index]) {
                        ctx.fillStyle = '#166534';
                        ctx.beginPath();
                        ctx.arc(goal, 24, 6, 0, Math.PI * 2);
                        ctx.fill();
                    }
                });

                lanes.forEach((lane) => {
                    lane.entities.forEach((entity: any) => {
                        const y = lane.y + 9;
                        ctx.fillStyle = entity.color;
                        this.drawRoundedRect(ctx, entity.x, y, entity.width, entity.height, lane.type === 'water' ? 14 : 12);
                        ctx.fill();

                        if (lane.type === 'road') {
                            ctx.fillStyle = 'rgba(255,255,255,0.8)';
                            this.drawRoundedRect(ctx, entity.x + 10, y + 6, entity.width - 20, 10, 6);
                            ctx.fill();
                        }

                        if (lane.type === 'water') {
                            ctx.fillStyle = 'rgba(255,255,255,0.18)';
                            ctx.fillRect(entity.x + 12, y + 6, entity.width - 24, 4);
                        }
                    });
                });

                if (!(respawnTimer > 0 && Math.floor(respawnTimer * 12) % 2 === 0)) {
                    ctx.fillStyle = '#4ade80';
                    this.drawRoundedRect(ctx, frog.x, frog.y, frogSize, frogSize, 12);
                    ctx.fill();

                    ctx.fillStyle = '#14532d';
                    ctx.beginPath();
                    ctx.arc(frog.x + 10, frog.y + 10, 3, 0, Math.PI * 2);
                    ctx.arc(frog.x + 20, frog.y + 10, 3, 0, Math.PI * 2);
                    ctx.fill();
                }

                if (gameOver) {
                    ctx.fillStyle = 'rgba(2, 6, 23, 0.72)';
                    ctx.fillRect(0, 0, width, height);
                    ctx.fillStyle = '#ffffff';
                    ctx.font = '700 30px SUIT';
                    ctx.textAlign = 'center';
                    ctx.fillText('Frog Rush 종료', width / 2, 258);
                    ctx.font = '500 16px SUIT';
                    ctx.fillText('Enter 또는 Reset으로 다시 시작', width / 2, 292);
                }
            }
        };
    }

    private createSnakeRuntime(): GameRuntime {
        const width = 720;
        const height = 576;
        const cell = 36;
        const cols = 18;
        const rows = 14;
        const offsetX = 36;
        const offsetY = 36;

        let snake: any[] = [];
        let direction = { x: 1, y: 0 };
        let nextDirection = { x: 1, y: 0 };
        let food = { x: 10, y: 7 };
        let moveTimer = 0;
        let moveDelay = 0.14;
        let score = 0;
        let gameOver = false;
        let status = '먹이를 먹고 길이를 늘리면서 속도 상승을 버텨보세요.';

        const speedValue = () => {
            return `${(0.14 / moveDelay).toFixed(1)}x`;
        };

        const placeFood = () => {
            let next = { x: 0, y: 0 };
            do {
                next = {
                    x: Math.floor(Math.random() * cols),
                    y: Math.floor(Math.random() * rows)
                };
            } while (snake.some((segment) => segment.x === next.x && segment.y === next.y));
            food = next;
        };

        const syncHud = (actionHint?: string) => {
            this.updateHud({
                score: score,
                best: this.commitBestScore('snake', score),
                detailLabel: '길이',
                detailValue: `${snake.length}칸`,
                modeLabel: '속도',
                modeValue: speedValue(),
                status: status,
                actionHint: gameOver ? 'Enter 또는 다시 시작으로 재도전하세요.' : (actionHint || '방향키 또는 화면 패드로 이동')
            });
        };

        const step = () => {
            direction = { x: nextDirection.x, y: nextDirection.y };

            const head = {
                x: snake[0].x + direction.x,
                y: snake[0].y + direction.y
            };

            const hitWall = head.x < 0 || head.x >= cols || head.y < 0 || head.y >= rows;
            const hitBody = snake.some((segment) => segment.x === head.x && segment.y === head.y);

            if (hitWall || hitBody) {
                gameOver = true;
                status = '벽이나 자기 몸에 닿았습니다.';
                syncHud();
                return;
            }

            snake.unshift(head);

            if (head.x === food.x && head.y === food.y) {
                score += 25;
                moveDelay = Math.max(0.065, moveDelay - 0.006);
                placeFood();
                status = '먹이를 확보했습니다. 속도가 조금 빨라졌습니다.';
                syncHud('코너를 너무 일찍 꺾지 않는 것이 핵심입니다.');
                return;
            }

            snake.pop();
        };

        const setDirection = (x: number, y: number) => {
            if (direction.x === -x && direction.y === -y) return;
            nextDirection = { x, y };
        };

        return {
            key: 'snake',

            reset: () => {
                snake = [
                    { x: 9, y: 7 },
                    { x: 8, y: 7 },
                    { x: 7, y: 7 },
                ];
                direction = { x: 1, y: 0 };
                nextDirection = { x: 1, y: 0 };
                moveTimer = 0;
                moveDelay = 0.14;
                score = 0;
                gameOver = false;
                placeFood();
                status = '먹이를 먹고 길이를 늘리면서 속도 상승을 버텨보세요.';
                syncHud();
            },

            keydown: (event: KeyboardEvent) => {
                if ((event.key === 'Enter' || event.code === 'Space') && gameOver) {
                    this.runtime?.reset();
                    return;
                }

                if (event.repeat) return;

                if (event.key === 'Enter') {
                    this.runtime?.reset();
                    return;
                }

                if (event.key === 'ArrowUp' || event.key === 'w' || event.key === 'W') setDirection(0, -1);
                if (event.key === 'ArrowDown' || event.key === 's' || event.key === 'S') setDirection(0, 1);
                if (event.key === 'ArrowLeft' || event.key === 'a' || event.key === 'A') setDirection(-1, 0);
                if (event.key === 'ArrowRight' || event.key === 'd' || event.key === 'D') setDirection(1, 0);
            },

            update: (dt: number) => {
                if (gameOver) return;

                moveTimer += dt;
                while (moveTimer >= moveDelay) {
                    moveTimer -= moveDelay;
                    step();
                }
            },

            draw: (ctx: CanvasRenderingContext2D) => {
                ctx.clearRect(0, 0, width, height);

                const gradient = ctx.createLinearGradient(0, 0, width, height);
                gradient.addColorStop(0, '#020617');
                gradient.addColorStop(1, '#111827');
                ctx.fillStyle = gradient;
                ctx.fillRect(0, 0, width, height);

                ctx.fillStyle = 'rgba(14, 165, 233, 0.12)';
                for (let x = 0; x < cols; x++) {
                    for (let y = 0; y < rows; y++) {
                        this.drawRoundedRect(ctx, offsetX + x * cell + 2, offsetY + y * cell + 2, cell - 4, cell - 4, 10);
                        ctx.fill();
                    }
                }

                ctx.fillStyle = '#f43f5e';
                this.drawRoundedRect(ctx, offsetX + food.x * cell + 6, offsetY + food.y * cell + 6, cell - 12, cell - 12, 12);
                ctx.fill();

                snake.forEach((segment, index) => {
                    ctx.fillStyle = index === 0 ? '#22c55e' : '#4ade80';
                    this.drawRoundedRect(ctx, offsetX + segment.x * cell + 4, offsetY + segment.y * cell + 4, cell - 8, cell - 8, 12);
                    ctx.fill();
                });

                if (gameOver) {
                    ctx.fillStyle = 'rgba(2, 6, 23, 0.75)';
                    ctx.fillRect(0, 0, width, height);
                    ctx.fillStyle = '#ffffff';
                    ctx.textAlign = 'center';
                    ctx.font = '700 30px SUIT';
                    ctx.fillText('Snake Grid 종료', width / 2, 258);
                    ctx.font = '500 16px SUIT';
                    ctx.fillText('Enter 또는 Reset으로 다시 시작', width / 2, 292);
                }
            }
        };
    }

    private createRunnerRuntime(): GameRuntime {
        const width = 720;
        const height = 576;
        const groundY = 448;

        let player = { x: 128, y: 0, width: 46, height: 52, vy: 0 };
        let obstacles: any[] = [];
        let clouds: any[] = [];
        let spawnTimer = 1.05;
        let speed = 280;
        let distance = 0;
        let cleared = 0;
        let gameOver = false;
        let lastHudMark = 0;
        let status = '점프 타이밍만으로 버티는 러너입니다. 속도가 조금씩 올라갑니다.';

        const syncHud = (actionHint?: string) => {
            this.updateHud({
                score: Math.floor(distance),
                best: this.commitBestScore('runner', distance),
                detailLabel: '속도',
                detailValue: `${(speed / 280).toFixed(1)}x`,
                modeLabel: '클리어',
                modeValue: `${cleared}개`,
                status: status,
                actionHint: gameOver ? 'Enter 또는 다시 시작으로 재도전하세요.' : (actionHint || '위 방향키, W 또는 Space로 점프')
            });
        };

        const resetClouds = () => {
            clouds = [
                { x: 80, y: 96, size: 34, speed: 18 },
                { x: 280, y: 74, size: 28, speed: 24 },
                { x: 560, y: 118, size: 38, speed: 16 },
            ];
        };

        const jump = () => {
            const onGround = player.y >= groundY - player.height - 1;
            if (!onGround) return;
            player.vy = -590;
        };

        const spawnObstacle = () => {
            const tall = Math.random() > 0.45;
            obstacles.push({
                x: width + 50,
                y: tall ? groundY - 72 : groundY - 52,
                width: tall ? 38 : 52,
                height: tall ? 72 : 52,
                counted: false,
                tone: tall ? '#f97316' : '#7c3aed'
            });
        };

        return {
            key: 'runner',

            reset: () => {
                player = { x: 128, y: groundY - 52, width: 46, height: 52, vy: 0 };
                obstacles = [];
                spawnTimer = 1.05;
                speed = 280;
                distance = 0;
                cleared = 0;
                gameOver = false;
                lastHudMark = 0;
                resetClouds();
                status = '점프 타이밍만으로 버티는 러너입니다. 속도가 조금씩 올라갑니다.';
                syncHud();
            },

            keydown: (event: KeyboardEvent) => {
                if ((event.key === 'Enter' || event.code === 'Space') && gameOver) {
                    this.runtime?.reset();
                    return;
                }

                if (event.key === 'Enter') {
                    this.runtime?.reset();
                    return;
                }

                if (event.repeat) return;

                if (event.key === 'ArrowUp' || event.key === 'w' || event.key === 'W' || event.code === 'Space' || event.key === ' ') {
                    jump();
                }
            },

            update: (dt: number) => {
                if (gameOver) return;

                speed += dt * 5;
                distance += dt * (speed * 0.06);

                const hudMark = Math.floor(distance / 10);
                if (hudMark !== lastHudMark) {
                    lastHudMark = hudMark;
                    syncHud('리듬을 유지하면서 더 멀리 달려보세요.');
                }

                spawnTimer -= dt;
                if (spawnTimer <= 0) {
                    spawnObstacle();
                    spawnTimer = Math.max(0.55, 1.18 - speed / 430) + Math.random() * 0.25;
                }

                player.vy += 1420 * dt;
                player.y += player.vy * dt;
                if (player.y > groundY - player.height) {
                    player.y = groundY - player.height;
                    player.vy = 0;
                }

                clouds.forEach((cloud) => {
                    cloud.x -= cloud.speed * dt;
                    if (cloud.x < -120) cloud.x = width + Math.random() * 180;
                });

                obstacles.forEach((obstacle) => {
                    obstacle.x -= speed * dt;
                    if (!obstacle.counted && obstacle.x + obstacle.width < player.x) {
                        obstacle.counted = true;
                        cleared += 1;
                        status = '장애물을 넘겼습니다. 속도가 계속 올라갑니다.';
                        syncHud('리듬을 잃지 않는 것이 핵심입니다.');
                    }
                });

                obstacles = obstacles.filter((obstacle) => obstacle.x + obstacle.width > -80);

                const hit = obstacles.some((obstacle) => {
                    return this.intersects(
                        { x: player.x + 6, y: player.y + 4, width: player.width - 12, height: player.height - 4 },
                        { x: obstacle.x, y: obstacle.y, width: obstacle.width, height: obstacle.height }
                    );
                });

                if (hit) {
                    gameOver = true;
                    status = '장애물에 걸렸습니다.';
                    syncHud();
                }
            },

            draw: (ctx: CanvasRenderingContext2D) => {
                ctx.clearRect(0, 0, width, height);

                const sky = ctx.createLinearGradient(0, 0, 0, height);
                sky.addColorStop(0, '#fef3c7');
                sky.addColorStop(0.5, '#fb923c');
                sky.addColorStop(1, '#7c2d12');
                ctx.fillStyle = sky;
                ctx.fillRect(0, 0, width, height);

                ctx.fillStyle = 'rgba(255,255,255,0.25)';
                ctx.beginPath();
                ctx.arc(580, 90, 46, 0, Math.PI * 2);
                ctx.fill();

                clouds.forEach((cloud) => {
                    ctx.fillStyle = 'rgba(255,255,255,0.65)';
                    this.drawRoundedRect(ctx, cloud.x, cloud.y, cloud.size * 2.1, cloud.size, cloud.size / 2);
                    ctx.fill();
                });

                ctx.fillStyle = 'rgba(255,255,255,0.12)';
                ctx.beginPath();
                ctx.moveTo(0, 380);
                ctx.lineTo(120, 260);
                ctx.lineTo(280, 382);
                ctx.lineTo(430, 290);
                ctx.lineTo(620, 398);
                ctx.lineTo(720, 320);
                ctx.lineTo(720, 576);
                ctx.lineTo(0, 576);
                ctx.closePath();
                ctx.fill();

                ctx.fillStyle = '#1f2937';
                ctx.fillRect(0, groundY, width, height - groundY);
                ctx.fillStyle = '#facc15';
                for (let x = 0; x < width; x += 44) {
                    ctx.fillRect(x, groundY + 42, 22, 8);
                }

                obstacles.forEach((obstacle) => {
                    ctx.fillStyle = obstacle.tone;
                    this.drawRoundedRect(ctx, obstacle.x, obstacle.y, obstacle.width, obstacle.height, 14);
                    ctx.fill();
                });

                ctx.fillStyle = '#111827';
                this.drawRoundedRect(ctx, player.x, player.y + 10, player.width, player.height - 10, 16);
                ctx.fill();
                ctx.fillStyle = '#fbbf24';
                ctx.beginPath();
                ctx.arc(player.x + 24, player.y + 8, 14, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = '#ffffff';
                ctx.beginPath();
                ctx.arc(player.x + 27, player.y + 7, 3, 0, Math.PI * 2);
                ctx.fill();

                if (gameOver) {
                    ctx.fillStyle = 'rgba(2, 6, 23, 0.72)';
                    ctx.fillRect(0, 0, width, height);
                    ctx.fillStyle = '#ffffff';
                    ctx.textAlign = 'center';
                    ctx.font = '700 30px SUIT';
                    ctx.fillText('Sky Runner 종료', width / 2, 258);
                    ctx.font = '500 16px SUIT';
                    ctx.fillText('Enter 또는 Reset으로 다시 시작', width / 2, 292);
                }
            }
        };
    }

    private cloneLanes(blueprints: any[], cell: number) {
        return blueprints.map((lane) => {
            return {
                ...lane,
                y: lane.row * cell,
                entities: lane.xs.map((x: number) => {
                    return {
                        x,
                        width: lane.width,
                        height: lane.height,
                        color: lane.color,
                    };
                })
            };
        });
    }
}

export default PageGamesComponent;